import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LiliButton, LiliButtonType } from '@lili/lili-frontend-common';
import { useTranslation } from 'react-i18next';
import SideBarItem from './SideBarItem';
import Icon from '../../generic-components/Icon';
import { LiliLogo } from '../../assets/svg';
import { IBlink, ISidebarLink } from '../../types';
import { useStore } from '../../store';
import { BIG_SCREEN_WIDTH, CARD_ACTION } from '../../constants/general';
import { getMostRelevantCard, getPrettyStatus } from '../../helpers/cardStatus.helper';
import { canUpgradeToUseFeature, validateFeatureFlag } from '../../services/featureFlagService';
import Button from '../../generic-components/Button';
import { ReactComponent as PlayIcon } from '../../assets/icons/svg/shared/ic-shared-play.svg';
import { trackMixPanel } from '../../services/mixpanel.service';
import { useMainSideBarData } from './MainSideBarData';
import { featureAccess, LimitedFeatureType } from '../../services/appAccess.service';
import { FeatureAccess } from '../../pages/AccountSettings/components/rolesAndPermissions/PermissionsList';

interface IProps {
  isResponsiveMenu?: boolean;
}

const SideBar = ({ isResponsiveMenu }: IProps) => {
  const {
    customer: { externalId: customerId },
  } = useStore((state) => state.summary);
  const { sidebarLinks } = useMainSideBarData();
  const isBigScreen = useStore((state) => state.isBigScreen);
  const { getPersonalAccount, summary } = useStore((state) => state);
  const cardObject = getMostRelevantCard(getPersonalAccount().cards);
  const showMyLiliCard =
    getPrettyStatus(cardObject) !== CARD_ACTION.PRETTY_STATUS.UNKNOWN &&
    getPrettyStatus(cardObject) !== CARD_ACTION.PRETTY_STATUS.NEEDS_FUND &&
    getPrettyStatus(cardObject) !== CARD_ACTION.PRETTY_STATUS.RETURN_TO_SENDER;
  const [isMenuOpen, setIsMenuOpen] = useState(isBigScreen);
  const { pathname } = useLocation();
  const [selectedLinkId, setSelectedLinkId] = useState<string>('dashboard');
  const [selectedSubLinkId, setSelectedSubLinkId] = useState<string>('');
  const [expandedSubMenuLinkId, setExpandedSubMenuLinkId] = useState<string>('');
  const [links, setLinks] = useState<ISidebarLink[]>(sidebarLinks);
  const navigate = useNavigate();
  const { t } = useTranslation();
  const productTourPermission = featureAccess(null, LimitedFeatureType.ProductTour);
  const { authorizedUserType } = useStore((state) => state.user);

  useEffect(() => {
    setLinks(sidebarLinks);
  }, [sidebarLinks]);

  const validateLinkData = (link: ISidebarLink) => {
    const mutateLink = { ...link };
    mutateLink.subLinks?.forEach((subLink: IBlink) => {
      if (featureAccess(subLink.featureFlagId || null, subLink.limitedFeatureType) === FeatureAccess.NONE) {
        if (subLink.featureFlagId) {
          // If user can upgrade to use the feature, then show the sublink
          subLink.shouldHide = subLink.upgradeToUseFeature ? !canUpgradeToUseFeature(subLink.featureFlagId) : true;
        } else {
          subLink.shouldHide = true;
        }
        return;
      }
      if (authorizedUserType && !!subLink.disabledForRoles?.length) {
        subLink.shouldHide = subLink.disabledForRoles.includes(authorizedUserType);
        return;
      }
      if (subLink.shouldValidateSubLink?.func) {
        const isValid = subLink.shouldValidateSubLink.func(summary?.business?.type || '', summary, subLink.actionFlag);
        subLink.shouldHide = !isValid;
      } else if (subLink.upgradeToUseFeature && subLink.featureFlagId) {
        subLink.shouldHide =
          !validateFeatureFlag(subLink.featureFlagId) && !canUpgradeToUseFeature(subLink.featureFlagId);
      } else {
        subLink.shouldHide = !!(subLink.featureFlagId && !validateFeatureFlag(subLink.featureFlagId));
      }
    });

    return mutateLink;
  };

  const validateLink = (link: ISidebarLink) => {
    if (featureAccess(null, link.limitedFeatureType) === FeatureAccess.NONE) {
      if (link.featureFlag && link.upgradeToUseFeature) {
        return canUpgradeToUseFeature(link.featureFlag);
      }
      return false;
    }
    if (authorizedUserType && !!link.disabledForRoles?.length) {
      return !link.disabledForRoles.includes(authorizedUserType);
    }
    if (link.actionFlagValue) {
      if (link.featureFlag) {
        return summary.actionFlags[link.actionFlagValue] && validateFeatureFlag(link.featureFlag);
      }
      return summary.actionFlags[link.actionFlagValue];
    }
    if (link.shouldValidateMasterLink?.func) {
      return link.shouldValidateMasterLink?.func();
    }
    if (!link.featureFlag) {
      return true;
    }
    if (validateFeatureFlag(link.featureFlag) || canUpgradeToUseFeature(link.featureFlag)) {
      return true;
    }
    return !validateFeatureFlag(link.featureFlag) || canUpgradeToUseFeature(link.featureFlag);
  };

  useEffect(() => {
    let newLinks = sidebarLinks;
    if (!showMyLiliCard && cardObject) {
      newLinks = sidebarLinks.filter((linkObject) => linkObject.linkName !== 'My Lili Card');
    }
    setLinks(newLinks);
  }, [showMyLiliCard]);

  useEffect(() => {
    if (isBigScreen) {
      setIsMenuOpen(true);
    } else {
      setIsMenuOpen(false);
    }
  }, [isBigScreen]);

  useEffect(() => {
    setSelectedSubLinkId('');
    if (!pathname || pathname === '/') {
      setSelectedLinkId('dashboard');
      setExpandedSubMenuLinkId('');
      return;
    }
    const menuItems = sidebarLinks.filter((link) => {
      const isSublinkIncluded = link.subLinks?.some((sublink) => {
        if (sublink.path === pathname) {
          setSelectedSubLinkId(sublink.id);
          return true;
        }
        return false;
      });
      return isSublinkIncluded || link.path === pathname;
    });
    if (menuItems[0]) {
      setSelectedLinkId(menuItems[0].id);
      setExpandedSubMenuLinkId(menuItems[0].id);
    } else {
      setSelectedLinkId(pathname);
    }
  }, [pathname, customerId]);

  const onDropdownClick = (link: ISidebarLink) => {
    if (link.callback) link.callback();

    if (link.subLinks) {
      setExpandedSubMenuLinkId(link.id);
      if (expandedSubMenuLinkId !== link.id) {
        setExpandedSubMenuLinkId(link.id);
      } else {
        setExpandedSubMenuLinkId('');
      }
    } else {
      setExpandedSubMenuLinkId('');
    }
  };

  const handleProductTourClick = () => {
    trackMixPanel('act on in app product tour', { user_id: customerId });
    window.open('https://lili.navattic.com/e51v0ml5', '_blank');
  };

  return (
    <div
      onMouseEnter={() => {
        if (!isResponsiveMenu) setIsMenuOpen(true);
      }}
      onMouseLeave={() => {
        if (window.innerWidth < BIG_SCREEN_WIDTH && !isResponsiveMenu) setIsMenuOpen(false);
      }}
      className={`fixed z-40 ${isMenuOpen ? 'w-[254px]' : 'w-[100px]'} shadow-sidebar ease-in-out duration-500 h-full bg-bw00 overflow-x-hidden`}
    >
      {links && links.length > 0 && (
        <div className='flex flex-col h-full'>
          <div className='py-[24px] flex items-center border-b border-bw03 w-full'>
            <Button
              id='navigateToDashboard'
              className='min-h-[48px]'
              onClick={() => {
                setExpandedSubMenuLinkId('');
                navigate('/dashboard');
              }}
              childElement={<Icon src={LiliLogo} className='ml-5 w-12 h-8' />}
            />
          </div>
          <div className='flex flex-col justify-between h-full'>
            <ul className='flex flex-col gap-3 mt-8 pb-[24px]'>
              {links.map(
                (link) =>
                  validateLink(link) && (
                    <SideBarItem
                      key={link.id}
                      menuItem={validateLinkData(link)}
                      isMenuOpen={isMenuOpen}
                      isNew={link.isNewBadge}
                      onDropdownClick={onDropdownClick}
                      selectedLinkId={selectedLinkId}
                      selectedSubLinkId={selectedSubLinkId}
                      expandedSubMenuLinkId={expandedSubMenuLinkId}
                    />
                  ),
              )}
            </ul>
            {isMenuOpen && !summary?.actionFlags?.accountFunded && productTourPermission !== FeatureAccess.NONE && (
              <div className='mx-[24px] w-[207px] pb-[24px]'>
                <LiliButton
                  id='product-tour'
                  title={isMenuOpen ? t('PRODUCT_TOUR') : ''}
                  className='h-[40px]'
                  onClick={handleProductTourClick}
                  type={LiliButtonType.SECONDARY}
                  icon={<PlayIcon className='text-text-brand-primary' />}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SideBar;
